import { CdkMonitorFocus } from '@angular/cdk/a11y';
import { NgTemplateOutlet } from '@angular/common';
import { AfterViewInit, Component, OnChanges, SimpleChanges, ViewEncapsulation, inject, input, output, viewChild } from '@angular/core';
import { FormsModule, NgControl, ValidatorFn } from '@angular/forms';

import { ApContentService } from '@ap/content';
import { ApFormErrorsComponent } from '@ap/form/errors';
import { ApIconComponent, AvailableIcons } from '@ap/icon';
import { AbstractInputComponent, ApOptions, InputmodeType } from '@ap/input';
import { ApInputConstraintService } from '@ap/input/constraint';
import { ApDateRangeValidator, ApMaxDateValidator, ApMinDateValidator, ApValidDateValidator } from '@ap/input/validators';

import { ApCalendarControlComponent } from './calendar/control/calendar-control.component';
import { ApDateInputDirective } from './calendar/date.directive';
import content from './date-input.content.json';
import type { ApCalendarView } from './date-input.model';

@Component({
    selector: 'ap-date-input',
    templateUrl: './date-input.component.html',
    styleUrls: ['../../../src/lib/input.component.scss'],
    encapsulation: ViewEncapsulation.Emulated,
    imports: [
        ApDateInputDirective,
        FormsModule,
        ApCalendarControlComponent,
        CdkMonitorFocus,
        ApIconComponent,
        ApFormErrorsComponent,
        NgTemplateOutlet
    ],
    providers: [ApInputConstraintService]
})
export class ApDateInputComponent extends AbstractInputComponent implements AfterViewInit, OnChanges {
    protected control = inject(NgControl, { self: true, optional: true });
    protected readonly inputConstraintService = inject(ApInputConstraintService);
    protected readonly contentService = inject(ApContentService);

    /**
     * Type of input (Standard HTML types)
     * @internal
     */
    readonly picker = viewChild<ApCalendarControlComponent>('picker');

    /**
     * Min date. If set validation will also be added
     */
    readonly minDate = input<Date>(undefined);

    /**
     * Function to filter the dates that are disabled
     */
    readonly dateFilter = input<(date: Date | null) => boolean>(undefined);

    /**
     * Max date. If set validation will also be added
     */
    readonly maxDate = input<Date>(undefined);

    /**
     * Date to start at. If left empty, will default to today
     */
    readonly startAt = input<Date>(undefined);

    /**
     * The view that the calendar should start in.
     * Options are 'month' | 'year' | 'multi-year'.
     * Defaults to 'month'
     */
    readonly startView = input<ApCalendarView>('month');

    /**
     * Emits selected year in multiyear view.
     * This doesn't imply a change on the selected date.
     */
    readonly yearSelected = output<Date>();

    /**
     * Emits selected month in year view.
     * This doesn't imply a change on the selected date.
     */
    readonly monthSelected = output<Date>();

    /**
     * Maxlength of a date is 10 (dd-MM-yyyy)
     */
    protected readonly maxlength = input<number>(10);

    /**
     * Characters that are stripped
     */
    protected readonly stripPattern = input<RegExp>(/[^0-9-\s]/g);

    /**
     * Inputmode (Standard HTML inputmode types)
     */
    protected readonly inputmode = input<InputmodeType>('numeric');

    /**
     * Date icon
     */
    protected readonly icon = input<AvailableIcons>('calendar');

    override readonly options = input<ApOptions>(null);

    /** @internal */
    public get dateValue(): Date {
        return this._dateValue;
    }

    /**
     * Date value can be set either by typing or by selecting a date from the datepicker
     * @internal
     */
    public set dateValue(val: Date) {
        this._dateValue = val;
        this.onChange(this._dateValue);
    }

    private _dateValue: Date;

    /**
     * The `hasValidator` function of angular requires the same instance of a validator function
     * that was set to `addValidators` to be passed when checking if the validator was set.
     * Otherwise it will always return false. Therefore we save and set the minDateValtion function in the property,
     * so we can successfully remove the validator if it set already, before setting a new validator
     */
    private _minDateValidation: ValidatorFn;

    /**
     * The `hasValidator` function of angular requires the same instance of a validator function
     * that was set to `addValidators` to be passed when checking if the validator was set.
     * Otherwise it will always return false. Therefore we save and set the maxDateValtion function in the property,
     * so we can successfully remove the validator if it set already, before setting a new validator
     */
    private _maxDateValidation: ValidatorFn;

    /**
     * The `hasValidator` function of angular requires the same instance of a validator function
     * that was set to `addValidators` to be passed when checking if the validator was set.
     * Otherwise it will always return false. Therefore we save and set the maxDateValtion function in the property,
     * so we can successfully remove the validator if it set already, before setting a new validator
     */
    private _dateRangeValidation: ValidatorFn;

    constructor() {
        super();
        this.setDefaultContent(content, 'date-input');
    }

    /**
     * Override onChange to set value type to Date
     */
    protected onChange: (value: Date) => void = () => {};

    /**
     * After init bind the control validation
     */
    public ngAfterViewInit(): void {
        this.dateRangeValidator();
        this.setDateValidation();
    }

    /**
     * On change of min or max date update the min or max date validation
     */
    public ngOnChanges(changes?: SimpleChanges): void {
        super.ngOnChanges(changes);
        if (changes?.minDate || changes?.maxDate) {
            this.dateRangeValidator();
        }
    }

    /**
     * Set valid date validation
     */
    private setDateValidation(): void {
        const control = this.control?.control;
        if (control) {
            control.addValidators([ApValidDateValidator.validate]);
            control.updateValueAndValidity();
        }
    }

    /**
     * Set min date validation
     */
    private setMinDateValidation(): void {
        const control = this.control?.control;
        if (control) {
            if (this._minDateValidation && control.hasValidator(this._minDateValidation)) {
                control.removeValidators(this._minDateValidation);
                this._minDateValidation = null;
            }
            const minDate = this.minDate();
            if (minDate) {
                this._minDateValidation = ApMinDateValidator.validate(minDate);
                control.addValidators([this._minDateValidation]);
            }
            control.updateValueAndValidity();
        }
    }

    /**
     * Set max date validation
     */
    private setMaxDateValidation(): void {
        const control = this.control?.control;
        if (control) {
            if (this._maxDateValidation && control.hasValidator(this._maxDateValidation)) {
                control.removeValidators(this._maxDateValidation);
                this._maxDateValidation = null;
            }
            const maxDate = this.maxDate();
            if (maxDate) {
                this._maxDateValidation = ApMaxDateValidator.validate(maxDate);
                control.addValidators([this._maxDateValidation]);
            }
            control.updateValueAndValidity();
        }
    }

    /**
     * Set date range validation
     */
    private setDateRangeValidation(): void {
        const control = this.control?.control;
        if (control) {
            if (this._dateRangeValidation && control.hasValidator(this._dateRangeValidation)) {
                control.removeValidators(this._dateRangeValidation);
                this._dateRangeValidation = null;
            }
            const minDate = this.minDate();
            const maxDate = this.maxDate();
            if (minDate && maxDate) {
                this._dateRangeValidation = ApDateRangeValidator.validate(minDate, maxDate);
                control.addValidators([this._dateRangeValidation]);
            }
            control.updateValueAndValidity();
        }
    }

    /**
     * set date range validation with the min and maxdate values
     */
    private dateRangeValidator(): void {
        const control = this.control?.control;
        if (!control) {
            return;
        }

        const minDate = this.minDate();
        const maxDate = this.maxDate();
        if (minDate && maxDate) {
            return this.setDateRangeValidation();
        } else if (minDate && !maxDate) {
            return this.setMinDateValidation();
        } else if (!minDate && maxDate) {
            return this.setMaxDateValidation();
        }
    }

    /**
     * Open date picker on icon click
     * Called on blur of input
     */
    protected handleIconClick(event: Event): void {
        event.stopPropagation();
        event.preventDefault();
        this.picker().open();
    }

    /**
     * Close date picker
     */
    protected close(): void {
        this.picker().close();
    }

    /**
     * Control value accessor function
     * Overwrite standard write value from AbstractComponent to set Date
     * @internal
     */
    public writeValue(value: Date): void {
        this.dateValue = value;
    }
}
